import {
  DealRoleType,
  RecentFilesQuery,
  useDashboardQuery,
  useRecentFilesQuery,
  useSendNotificationMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { useMemo, useState } from "react";
import {
  formatDistanceToNow,
  formatDistanceToNowStrict,
  fromUnixTime,
  getUnixTime,
  subDays,
  subHours,
} from "date-fns";
import Loading from "../../components/Loading";
import { Card } from "../../components/Card";
import { Link, NavLink, useParams } from "react-router-dom";
import { Avatar } from "../../components/account/Avatar";
import { RecentActivity } from "../../components/activity/RecentActivity";
import { UseQueryResult } from "@tanstack/react-query";
import { FileIcon } from "../../components/FileIcon";
import {
  FolderIcon,
  LockKeyholeIcon,
  MessageCircleQuestionIcon,
} from "lucide-react";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { usePermissionCheck } from "@/src/hooks/usePermissionCheck";
import { SellerDashboard } from "./SellerDashboard";
import { InternalDashboard } from "./InternalDashboard";
import { useFeatureFlagEnabled } from "posthog-js/react";

export function Dashboard() {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  return (
    <>
      <PermissionCheck input={{
        permissionKey: "deal:is_seller",
        dealID: activeDealId,
      }} fallback={<DashboardContent dealId={activeDealId} />}>
        <SellerDashboard />
      </PermissionCheck>
    </>
  )
}

function DashboardContent(props: { dealId: string }) {
  const [showRemindAdmin, setShowRemindAdmin] = useState(true);

  const isInternal = useFeatureFlagEnabled("internal");

  const client = useGqlClient();
  const dashboardQuery = useDashboardQuery(client, {
    dealId: props.dealId,
    questionsInput: {
      filters: {
        answered: false,
        limit: 20,
      },
    },
  });
  const sendNotification = useSendNotificationMutation(client);

  const hasQuestionsAccess = usePermissionCheck({
    permissionKey: "questions:has_access",
  });
  const hasAccessSectionAccess = usePermissionCheck({
    permissionKey: "access:has_access",
    roleType: DealRoleType.Internal,
  });
  // Recent files card is always shown; the other two are permission-gated.
  // Compute visible card count so we can collapse to a 2-col grid for guests
  // who don't have the Access section.
  const visibleCardCount =
    1 + (hasQuestionsAccess ? 1 : 0) + (hasAccessSectionAccess ? 1 : 0);
  const gridColsClass =
    visibleCardCount >= 3
      ? "xl:grid-cols-3"
      : visibleCardCount === 2
        ? "xl:grid-cols-2"
        : "xl:grid-cols-1";

  const guestDealAccounts =
    dashboardQuery.data?.deal.guestGroups.flatMap((group) => {
      return group.accounts.map((dealAccount) => {
        return {
          account: dealAccount.account,
          dealAccount: dealAccount,
          group: group,
        };
      });
    }) || [];

  if (dashboardQuery.error) {
    return <div>Something went wrong</div>;
  }

  if (dashboardQuery.isPending || !dashboardQuery.data) {
    return (
      <div className="flex-1 ">
        <Loading />
      </div>
    );
  }

  let recentUnansweredQuestions = dashboardQuery.data.questions.questions;

  if (isInternal === true) {
    return <div>
      <InternalDashboard />
    </div>
  }

  return (
    <div className="max-h-[calc(100vh-150px)] overflow-y-scroll rounded-b-md p-2">
      <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
        <DataRoomCardContent />

        <PermissionCheck input={{ permissionKey: "questions:has_access" }}>
          <div className="w-full">
            <Card>
              <div className="h-60 flex flex-col">
                <DashboardCardHeader
                  icon={<MessageCircleQuestionIcon className="w-4 h-4 text-gray-500" />}
                  title="Unanswered questions"
                  countBadge={recentUnansweredQuestions.length}
                />
                <div className="overflow-y-auto no-scrollbar flex flex-col flex-1">
                  {recentUnansweredQuestions.length === 0 ? (
                    <DashboardCardEmpty message="No recent unanswered questions" />
                  ) : (
                    <div>
                      {recentUnansweredQuestions.map((question) => (
                        <Link
                          key={question.id}
                          to={`/deal/${props.dealId}/questions/${question.id}`}
                          className="block hover:bg-gray-50 transition-colors px-3 py-2"
                        >
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="flex-shrink-0 mt-0.5">
                              <Avatar account={question.createdBy} size="s" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-800 leading-tight truncate">
                                {question.title}
                              </p>
                              <p className="text-xs text-gray-500 leading-tight mt-0.5 truncate">
                                {question.createdBy.name} ·{" "}
                                {formatDistanceToNow(
                                  fromUnixTime(question.createdAt),
                                  { addSuffix: true },
                                )}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </PermissionCheck>
        <PermissionCheck
          input={{
            permissionKey: "access:has_access",
            roleType: DealRoleType.Internal,
          }}
        >
          <div className="w-full">
            <Card>
              <div className="h-60 flex flex-col">
                <DashboardCardHeader
                  icon={<LockKeyholeIcon className="w-4 h-4 text-gray-500" />}
                  title="Access"
                  countBadge={guestDealAccounts.length}
                  right={
                    <p className="text-xs text-gray-500/80">Guest accounts</p>
                  }
                />
                <div className="overflow-y-auto no-scrollbar flex flex-col flex-1">
                  {guestDealAccounts.length === 0 ? (
                    <DashboardCardEmpty message="No guest accounts" />
                  ) : (
                    <div>
                      {guestDealAccounts.map((account) => (
                        <NavLink
                          key={account.account.id}
                          to={`/deal/${props.dealId}/access/account/${account.dealAccount.id}`}
                          className="block hover:bg-gray-50 transition-colors px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-2 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <Avatar size="s" account={account.account} />
                              <p className="text-sm font-semibold text-gray-800 leading-tight truncate">
                                {account.account.name}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500/80 whitespace-nowrap flex-shrink-0">
                              {account.group.name}
                            </p>
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </PermissionCheck>
      </div>

      <PermissionCheck input={{ permissionKey: "deal_activity:has_access" }}>
        <RecentActivity dealId={props.dealId} />
      </PermissionCheck>
    </div>
  );
}

function DashboardCardHeader(props: {
  icon: React.ReactNode;
  title: string;
  countBadge?: number;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-gray-200">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex-shrink-0">{props.icon}</div>
        <p className="text-gray-700 text-sm font-semibold truncate">
          {props.title}
        </p>
        {typeof props.countBadge === "number" ? (
          <span className="text-[11px] leading-none text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
            {props.countBadge}
          </span>
        ) : null}
      </div>
      {props.right ? <div className="flex-shrink-0 ml-2">{props.right}</div> : null}
    </div>
  );
}

function DashboardCardEmpty(props: { message: string }) {
  return (
    <div className="flex justify-center flex-1 items-center px-4 text-center">
      <p className="text-gray-500 text-xs">{props.message}</p>
    </div>
  );
}


// function BuyerCard(props: {
//   query: UseQueryResult<DashboardQuery, unknown>;
//   buyer: DashboardQuery["deal"]["buyers"][0];
// }) {
//   const buyer = props.buyer;
//   const dealFirmGroupPresence = useSelector((state: AppState) =>
//     presenceSelectors.dealFirmGroupPresence(state, buyer.id),
//   );

//   if (!props.query.data) {
//     return null;
//   }

//   return (
//     <div key={buyer.id} className="min-w-72">
//       <NavLink to={`/buyers/${buyer.id}`}>
//         <Card padding="m">
//           <div className="flex items-center justify-between">
//             <p className="font-semibold text-gray-700 text-sm">{buyer.name}</p>
//             <DealFirmGroupStatus
//               dealFirmGroupId={buyer.id}
//               dealFirmGroup={buyer}
//             />
//           </div>
//           <div className="flex space-x-0.5 mt-2">
//             <DealProgress
//               dealStages={props.query.data.deal.dealStages}
//               currentDealStage={buyer.currentDealStage}
//               currentDealStageStatus={buyer.currentDealStageStatus}
//               mode="compact"
//             />
//           </div>
//           <div className="mt-4 gap-1 grid grid-cols-2">
//             <p className="text-xs font-semibold text-gray-600">Questions</p>
//             <p className="text-xs text-gray-500 text-right">
//               {buyer.questions.length}
//             </p>

//             <p className="text-xs font-semibold text-gray-600">
//               Data Room permission level
//             </p>
//             <p className="text-xs text-gray-500 text-right">
//               {buyer.dataRoomPermission.name}
//             </p>

//             <p className="text-xs font-semibold text-gray-600">Last activity</p>
//             <p className="text-xs text-gray-500 text-right">
//               {buyer.activity.length === 0 ? (
//                 "No activity"
//               ) : (
//                 <>
//                   {formatDistanceToNowStrict(
//                     fromUnixTime(buyer.activity[0].createdAt),
//                     { addSuffix: true },
//                   )}
//                 </>
//               )}
//             </p>

//             <p className="text-xs font-semibold text-gray-600">
//               Last file upload
//             </p>
//             <p className="text-xs text-gray-500 text-right">
//               {buyer.files.length === 0 ? (
//                 <p className="text-xs text-gray-500">No files</p>
//               ) : (
//                 <div className="flex justify-end">
//                   <FilePill
//                     id={
//                       buyer.files.sort((a, b) => a.createdAt - b.createdAt)[0]
//                         .id
//                     }
//                     name={
//                       buyer.files.sort((a, b) => a.createdAt - b.createdAt)[0]
//                         .name
//                     }
//                     type={
//                       buyer.files.sort((a, b) => a.createdAt - b.createdAt)[0]
//                         .fileType
//                     }
//                   />
//                 </div>
//               )}
//             </p>
//           </div>
//         </Card>
//       </NavLink>
//     </div>
//   );
// }

// function DealFirmGroupStatus(props: {
//   dealFirmGroupId: string;
//   dealFirmGroup: DashboardQuery["deal"]["buyers"][0];
// }) {
//   const dealFirmGroupPresence = useSelector((state: AppState) =>
//     presenceSelectors.dealFirmGroupPresence(state, props.dealFirmGroupId),
//   );

//   if (props.dealFirmGroup.dealAccessStatus != DealAccessStatus.Granted) {
//     return (
//       <div className="flex items-center">
//         <LockClosedIcon className="w-4 h-4 text-gray-500" />
//         <p className="text-xs text-gray-500">No deal access</p>
//       </div>
//     );
//   }

//   if (
//     (!dealFirmGroupPresence ||
//       dealFirmGroupPresence.activeUsers.length === 0) &&
//     props.dealFirmGroup.lastSeen
//   ) {
//     const lastSeen = fromUnixTime(props.dealFirmGroup.lastSeen);

//     if (differenceInDays(new Date(), lastSeen) > 7) {
//       return (
//         <div className="flex items-center gap-x-1.5">
//           <p className="text-xs text-gray-500">Inactive</p>
//           <Pulse color="gray" animate />
//         </div>
//       );
//     }

//     return (
//       <div className="flex items-center gap-x-1.5">
//         <p className="text-xs text-gray-500">
//           Last seen{" "}
//           {formatDistanceToNowStrict(
//             fromUnixTime(props.dealFirmGroup.lastSeen),
//             { addSuffix: true },
//           )}
//         </p>
//         <Pulse color="gray" />
//       </div>
//     );
//   }

//   return (
//     <div className="flex items-center gap-x-1.5">
//       <p className="text-xs text-green-500">
//         {dealFirmGroupPresence && dealFirmGroupPresence.activeUsers
//           ? dealFirmGroupPresence.activeUsers.length
//           : 0}{" "}
//         online
//       </p>
//       <Pulse color="green" animate />
//     </div>
//   );
// }

const RECENT_FILES_PRESETS = [
  { id: "1d", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
] as const;

type RecentFilesSince = (typeof RECENT_FILES_PRESETS)[number]["id"];

type RecentFile = RecentFilesQuery["deal"]["dataRoom"]["recentFiles"][number];

function getSinceCutoffUnix(since: RecentFilesSince): number | null {
  const now = new Date();
  switch (since) {
    case "1d":
      return getUnixTime(subHours(now, 24));
    case "7d":
      return getUnixTime(subDays(now, 7));
    case "30d":
      return getUnixTime(subDays(now, 30));

  }
}

function sortRecentFilesByNewest(files: RecentFile[]): RecentFile[] {
  return [...files].sort((a, b) => b.createdAt - a.createdAt);
}

function filterRecentFilesSince(
  files: RecentFile[],
  since: RecentFilesSince,
): RecentFile[] {
  const cutoff = getSinceCutoffUnix(since);
  if (cutoff === null) {
    return files;
  }
  return files.filter((file) => file.createdAt >= cutoff);
}

function formatFolderPath(
  parentFolders: ReadonlyArray<{ name: string }>,
): { display: string; full: string } {
  // parentFolders comes back leaf-parent first, so reverse to get root → parent
  const ordered = [...parentFolders].reverse();
  const names = ordered.map((folder) => (folder.name === "" ? "Home" : folder.name));

  if (names.length === 0) {
    return { display: "Home", full: "Home" };
  }

  const full = names.join(" / ");

  if (names.length <= 2) {
    return { display: full, full };
  }

  const display = `${names[0]} / … / ${names[names.length - 1]}`;
  return { display, full };
}

function DataRoomCardContent() {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const recentFilesQuery = useRecentFilesQuery(client, {
    dealId: activeDealId ?? "",
  });

  const [since, setSince] = useState<RecentFilesSince>("30d");

  const allFiles = recentFilesQuery.data?.deal.dataRoom.recentFiles ?? [];
  const filteredFiles = useMemo(
    () => filterRecentFilesSince(sortRecentFilesByNewest(allFiles), since),
    [allFiles, since],
  );

  return (
    <div className="w-full">
      <Card>
        <div className="h-60 flex flex-col">
          <DashboardCardHeader
            icon={<FolderIcon className="w-4 h-4 text-gray-500" />}
            title="Recent documents"
            countBadge={
              recentFilesQuery.isPending ? undefined : filteredFiles.length
            }

            right={<div className="flex items-center gap-1 flex-shrink-0">
              {RECENT_FILES_PRESETS.map((preset) => (
                <RecentFilesFilterPill
                  key={preset.id}
                  label={preset.label}
                  active={since === preset.id}
                  onClick={() => setSince(preset.id)}
                />
              ))}
            </div>}
          />



          <div className="overflow-y-auto no-scrollbar flex flex-col flex-1">
            <DataRoomCardContentInner
              query={recentFilesQuery}
              files={filteredFiles}
              since={since}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

function RecentFilesFilterPill(props: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={
        props.active
          ? "text-xs font-medium px-2 py-0.5 rounded-full bg-gray-900 text-white transition-colors"
          : "text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
      }
    >
      {props.label}
    </button>
  );
}

function DataRoomCardContentInner(props: {
  query: UseQueryResult<RecentFilesQuery, unknown>;
  files: RecentFile[];
  since: RecentFilesSince;
}) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  if (props.query.error) {
    return (
      <div className="flex justify-center flex-1 items-center">
        <p className="text-gray-500 text-xs">Something went wrong</p>
      </div>
    );
  }

  if (props.query.isPending || !props.query.data) {
    return (
      <div className="flex justify-center flex-1 items-center">
        <Loading />
      </div>
    );
  }

  if (props.files.length === 0) {
    const message =
      "No recent files"
    return <DashboardCardEmpty message={message} />;
  }

  return (
    <div>
      {props.files.map((file) => {
        const folderPath = formatFolderPath(file.parentFolders);
        return (
          <NavLink
            key={file.id}
            to={`/deal/${activeDealId}/documents/folder/${file.folderId}/file/${file.id}`}
            className="block hover:bg-gray-50 transition-colors px-3 py-2"
          >
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="min-w-0 flex-1">
                <p
                  className="text-xs text-gray-500 leading-tight truncate"
                  title={folderPath.full}
                >
                  {folderPath.display}
                </p>
                <div className="mt-0.5 flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0">
                    <FileIcon fileType={file.fileType} size="s" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800 leading-tight truncate min-w-0 flex-1">
                    {file.name}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500/80 whitespace-nowrap flex-shrink-0">
                {formatDistanceToNowStrict(fromUnixTime(file.createdAt), {
                  addSuffix: true,
                })}
              </p>
            </div>
          </NavLink>
        );
      })}
    </div>
  );
}
